// A file's comments where it is shown (RFC-054 §7, byj-1wv.70.6): the
// threads on the version and file shown, by line — each its first comment,
// its replies, a reply box, and Resolve for those who may — and, with a line
// picked, the box that begins a thread on it. A thread a link names is lit
// and brought into view. What is said on other versions or files is counted.
import { useEffect, useRef, useState, type FormEvent } from "react";
import type { FileComment } from "../api";
import { t, when, phrases } from "../i18n";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** The threads of these comments, each its first and its replies, by line. */
export function threadsOf(comments: FileComment[]): { root: FileComment; replies: FileComment[] }[] {
  const roots = comments.filter((c) => !c.parent_id);
  return roots
    .map((root) => ({ root, replies: comments.filter((c) => c.parent_id === root.id) }))
    .sort((a, b) => a.root.line - b.root.line || a.root.created_at.localeCompare(b.root.created_at));
}

/** How many threads each line has, of comments on one version and file. */
export function marksOf(comments: FileComment[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const c of comments) if (!c.parent_id) m.set(c.line, (m.get(c.line) ?? 0) + 1);
  return m;
}

function Comment({ c }: { c: FileComment }) {
  return (
    <div className="file-comment">
      <div className="row wrap small">
        <strong>{c.author.name || t("common:someone")}</strong>
        <span className="muted">{when(c.created_at)}</span>
      </div>
      <p className="pre-wrap flush">{c.body}</p>
    </div>
  );
}

function Say({ placeholder, action, send, onCancel, autoFocus }: {
  placeholder: string;
  action: string;
  send: (body: string) => Promise<void>;
  onCancel?: () => void;
  autoFocus?: boolean;
}) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await send(body.trim());
      setBody("");
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="stack tight" onSubmit={(e) => void submit(e)}>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder={placeholder} aria-label={placeholder} rows={2} maxLength={4000} autoFocus={autoFocus} />
      {error && <p className="error flush">{t("common:error", { m: error })}</p>}
      <div className="row">
        <button type="submit" className="small primary" disabled={busy || !body.trim()}>
          {action}
        </button>
        {onCancel && (
          <button type="button" className="small" onClick={onCancel}>
            {t("common:cancel")}
          </button>
        )}
      </div>
    </form>
  );
}

export function FileComments({ comments, others, line, path, lit, canResolve, onCancel, begin, reply, resolve }: {
  /** the comments on the version and file shown */
  comments: FileComment[];
  /** how many threads are on other versions or files */
  others: number;
  /** the line picked to begin a thread on, if any */
  line?: number;
  /** the file in a folder they are on */
  path?: string;
  /** the thread a link names */
  lit?: string | null;
  canResolve: (root: FileComment) => boolean;
  onCancel: () => void;
  begin: (line: number, body: string) => Promise<void>;
  reply: (root: FileComment, body: string) => Promise<void>;
  resolve: (root: FileComment, resolved: boolean) => Promise<void>;
}) {
  const threads = threadsOf(comments);
  const litRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    litRef.current?.scrollIntoView?.({ block: "center" });
  }, [lit, comments.length]);
  return (
    <section aria-labelledby="file-comments" className="stack file-comments" data-testid="file-comments">
      <div className="file-comments-head">
        <h2 id="file-comments" className="flush">
          {t("files:fileComments")} <span className="tab-count">{threads.length}</span>
        </h2>
        {/* where a comment is begun, said wherever the lines are to press */}
        {line === undefined && <p className="muted small flush">{t("files:fileCommentHow")}</p>}
      </div>
      {line !== undefined && (
        <div className="card pad stack tight" data-testid="file-new-comment">
          <strong className="small">{phrases.filePlace(line, path)}</strong>
          <Say placeholder={t("files:fileCommentPlaceholder")} action={t("files:fileComment")} send={(body) => begin(line, body)} onCancel={onCancel} autoFocus />
        </div>
      )}
      {threads.length === 0 && line === undefined && <p className="muted small flush">{t("files:fileNoComments")}</p>}
      <ul className="file-threads">
        {threads.map(({ root, replies }) => {
          const isLit = lit === root.id || replies.some((r) => r.id === lit);
          return (
            <li
              key={root.id}
              ref={isLit ? litRef : undefined}
              className={`card pad stack tight file-thread${root.resolved ? " resolved" : ""}${isLit ? " lit" : ""}`}
              data-testid={`thread-${root.id}`}
            >
              <div className="row wrap">
                <code className="small grow">{phrases.filePlace(root.line, root.path)}</code>
                {root.resolved && <span className="chip chip-ok">{t("files:fileResolved")}</span>}
                {canResolve(root) && (
                  <button className="small" onClick={() => void resolve(root, !root.resolved)}>
                    {root.resolved ? t("files:fileReopen") : t("files:fileResolve")}
                  </button>
                )}
              </div>
              <Comment c={root} />
              {replies.map((r) => (
                <Comment key={r.id} c={r} />
              ))}
              {!root.resolved && <Say placeholder={t("files:fileReplyPlaceholder")} action={t("files:fileReply")} send={(body) => reply(root, body)} />}
            </li>
          );
        })}
      </ul>
      {others > 0 && <p className="muted small flush">{t("files:fileOtherVersions", { count: others })}</p>}
    </section>
  );
}
